import { GlobalVariables } from 'app/services/globals';
import { ILogger } from './ILogger';
import { EventRow } from './logger.event-row';

export let pageLoadProperties: IPageLoadProperties = {};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNavigationTimingEntries(): Record<string, any> {
    const navigationTiming = performance.getEntriesByType(
        'navigation'
    )[0] as PerformanceNavigationTiming;
    if (!navigationTiming) {
        return { error: 'Navigation timing is not available.' };
    }
    const timingFields: Array<keyof PerformanceNavigationTiming> = [
        'redirectCount',
        'redirectStart',
        'fetchStart',
        'requestStart',
        'responseStart',
        'responseEnd',
        'domInteractive',
        'domContentLoadedEventStart',
        'domComplete',
        'loadEventStart',
        'loadEventEnd',
        'nextHopProtocol',
        'transferSize'
    ];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: Record<string, any> = {};
    for (const field of timingFields) {
        const v = navigationTiming[field];
        if (typeof v === 'number') {
            // Trim excessive fractions.
            result[field] = Math.round(v * 1000) / 1000;
        } else if (!!v) {
            result[field] = v;
        }
    }
    return result;
}

function getJsResouceTimingEntries(pageLoadProperties: IPageLoadProperties): Array<Partial<PerformanceResourceTiming>> {
    const resourceTimings = performance.getEntriesByType('resource');
    const result: Array<Partial<PerformanceResourceTiming>> = [];
    if (!resourceTimings) {
        return [];
    }
    resourceTimings.forEach((r) => {
        const timing = r as PerformanceResourceTiming;
        const {
            name,
            fetchStart,
            requestStart,
            responseStart,
            responseEnd,
            transferSize
        } = timing;

        const regx = /(main|index\.bundle).*js$/;
        if (r && r.name && regx.test(r.name)) {
            result.push({
                name,
                fetchStart,
                requestStart,
                responseStart,
                responseEnd,
                transferSize
            });
        }

        // Only logs the resource timings during extensionLoadEnd and authGuardStart to investigate the bottleneck of PLT and reduce log length
        if (fetchStart > pageLoadProperties.extensionLoadEnd && responseEnd < pageLoadProperties.authGuardStart) {
            result.push({
                name: `route-${timing.name}`,
                fetchStart,
                requestStart,
                responseStart,
                responseEnd,
                transferSize
            });
        }
    });

    return result;
}

export class A365PageLoadEventRow extends EventRow {
    constructor(private page: string) {
        super('a365.pageload', pageLoadProperties);
        this.addToProperties('page', this.page);
        this.addToProperties('pageLoaded', performance.now());
        this.addToProperties('navigationTiming', getNavigationTimingEntries());
        this.addToProperties('resourceTiming', getJsResouceTimingEntries(pageLoadProperties));
        this.addToProperties('timeOrigin', performance.timeOrigin);
    }

    public pushToAppInsights(): void {
        setTimeout(() => {
            if (GlobalVariables.enableAriaLogger) {
                oneDSWeb.trackEvent(this.generateLoggingEntry());
                oneDSWeb?.getPostChannel()?.flush();
            } else {
                appInsights.trackEvent(this.eventName, this.getStringifiedProperties());
                appInsights.flush();
            }
        }, 0);
    }
}

interface IPageLoadProperties {
    htmlLoaded?: string;
    mainJsLoaded?: number;

    // Load extension check points
    extensionLoadStart?: number;
    extensionManifestLoaded?: number;
    extensionDepsLoaded?: number;
    extensionLoadEnd?: number;

    // Auth guard check points
    authGuardStart?: number;
    authGuardEnd?: number;

    // Git guard check points
    gitGuardStart?: number;
    gitGuardEnd?: number;

    // Workspace guard check points
    workspaceGuardStart?: number;
    workspaceGuardEnd?: number;
    workspaceGuardFailed?: number;
    fetchUserRoleStart?: number;
    fetchUserRoleEnd?: number;
    checkFeatureStart?: number;
    checkFeatureEnd?: number;

    authoringSidebarLoaded?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bootstrapInfomation?: any;
    tokenTimings?: IAcquireTokenTiming[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    routerMetrics?: any;
}

interface IAcquireTokenTiming {
    authArea: string | string[];
    errorCode?: string;
    subError?: string;
}

export function logA365PageLoadEvent(logger: ILogger, page: string): void {
    if (pageLoadProperties) {
        logger.logEvent(new A365PageLoadEventRow(page));
        pageLoadProperties = null;
    }
}
